import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api, resolveMediaUrl } from '../../utils/api';
import { useNotification } from '../../contexts/NotificationContext';
import ProtectedRoute from '../../components/ProtectedRoute';
import AdminLayout, { useTheme } from '../../components/AdminLayout';
import NumberInput from '../../components/NumberInput';
import Skeleton from '../../components/Skeleton';

function ProductsContent() {
  const { t } = useTranslation();
  const { showAlert, showConfirm } = useNotification();
  const { T, S } = useTheme();
  const [products, setProducts] = useState([]);
  const [shops, setShops] = useState([]);
  const [allBranches, setAllBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [selectedShopId, setSelectedShopId] = useState('');
  const [form, setForm] = useState({
    branch_id: '', name: '', price: '', category: '', photo_url: null,
  });
  const [pendingPhotoFile, setPendingPhotoFile] = useState(null);

  const [productCategories, setProductCategories] = useState([]);
  const [categoryPanelShopId, setCategoryPanelShopId] = useState('');
  const [categoryPanelBranchId, setCategoryPanelBranchId] = useState('');
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [categoryForm, setCategoryForm] = useState({ name: '' });
  const [editingCategory, setEditingCategory] = useState(null);

  const filteredBranches = allBranches.filter((b) => !selectedShopId || b.shop_id === parseInt(selectedShopId, 10));
  const categoryPanelBranches = allBranches.filter((b) => !categoryPanelShopId || b.shop_id === parseInt(categoryPanelShopId, 10));

  const fetchData = async () => {
    try {
      const [prodData, shopData, branchData] = await Promise.all([
        api.getAdminProducts(),
        api.getAdminShops(),
        api.getBranches(),
      ]);
      setProducts(prodData);
      setShops(shopData);
      setAllBranches(branchData);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchProductCategories = async (branchId) => {
    if (!branchId) {
      setProductCategories([]);
      return;
    }
    try {
      const data = await api.getAdminProductCategories(parseInt(branchId, 10));
      setProductCategories(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
      setProductCategories([]);
    }
  };

  useEffect(() => {
    if (categoryPanelBranchId) {
      fetchProductCategories(categoryPanelBranchId);
    } else {
      setProductCategories([]);
    }
  }, [categoryPanelBranchId]);

  useEffect(() => {
    if (form.branch_id) {
      fetchProductCategories(form.branch_id);
    }
  }, [form.branch_id]);

  const branchById = (id) => allBranches.find((b) => b.id === id);
  const shopOfBranch = (branchId) => {
    const b = branchById(branchId);
    return shops.find((s) => s.id === b?.shop_id)?.name ?? '—';
  };

  const openCreate = () => {
    setEditing('create');
    setSelectedShopId('');
    setForm({
      branch_id: '', name: '', price: '', category: '', photo_url: null,
    });
    setPendingPhotoFile(null);
  };

  const openEdit = (p) => {
    const branch = branchById(p.branch_id);
    setSelectedShopId(String(branch?.shop_id ?? ''));
    setEditing(p.id);
    setForm({
      branch_id: p.branch_id,
      name: p.name,
      price: p.price,
      category: p.category ?? '',
      photo_url: p.photo_url ?? null,
    });
    setPendingPhotoFile(null);
  };

  const handleShopChange = (shopId) => {
    setSelectedShopId(shopId);
    setForm((f) => ({ ...f, branch_id: '', category: '' }));
  };

  const uploadPhoto = async (e) => {
    if (!editing || editing === 'create' || !e.target?.files?.[0]) return;
    const file = e.target.files[0];
    if (!file.type.startsWith('image/')) {
      showAlert(t('manager.pleaseSelectImageFile'));
      return;
    }
    try {
      const fd = new FormData();
      fd.append('file', file);
      const updated = await api.uploadAdminProductPhoto(editing, fd);
      setForm((f) => ({ ...f, photo_url: updated.photo_url }));
      fetchData();
    } catch (err) {
      showAlert(err.message);
    }
    e.target.value = '';
  };

  const deletePhoto = () => {
    if (!editing || editing === 'create' || !form.photo_url) return;
    showConfirm(t('admin.deletePhotoConfirm', 'Delete this photo?'), async () => {
      try {
        const updated = await api.deleteAdminProductPhoto(editing);
        setForm((f) => ({ ...f, photo_url: updated.photo_url ?? null }));
        fetchData();
      } catch (err) {
        showAlert(err.message);
      }
    });
  };

  const openAddCategory = () => {
    setEditingCategory(null);
    setCategoryForm({ name: '' });
    setShowCategoryModal(true);
  };

  const openEditCategory = (c) => {
    setEditingCategory(c.id);
    setCategoryForm({ name: c.name });
    setShowCategoryModal(true);
  };

  const saveCategory = async () => {
    const bid = categoryPanelBranchId || form.branch_id;
    if (!bid || !categoryForm.name.trim()) return;
    try {
      if (editingCategory) {
        await api.updateAdminProductCategory(editingCategory, { name: categoryForm.name.trim() });
      } else {
        await api.createAdminProductCategory({ branch_id: parseInt(bid, 10), name: categoryForm.name.trim() });
      }
      setShowCategoryModal(false);
      fetchProductCategories(bid);
    } catch (err) {
      showAlert(err.message);
    }
  };

  const removeCategory = (id) => {
    showConfirm(t('admin.deleteProductCategoryConfirm'), async () => {
      try {
        await api.deleteAdminProductCategory(id);
        const bid = categoryPanelBranchId || form.branch_id;
        if (bid) fetchProductCategories(bid);
        setShowCategoryModal(false);
      } catch (err) {
        showAlert(err.message);
      }
    });
  };

  const save = async () => {
    if (!form.branch_id) {
      showAlert(t('admin.pleaseSelectBranch'));
      return;
    }
    try {
      const payload = {
        branch_id: parseInt(form.branch_id, 10),
        name: form.name,
        price: parseFloat(form.price),
        category: form.category || null,
      };
      if (editing === 'create') {
        const created = await api.createAdminProduct(payload);
        if (pendingPhotoFile) {
          const fd = new FormData();
          fd.append('file', pendingPhotoFile);
          await api.uploadAdminProductPhoto(created.id, fd);
        }
      } else {
        await api.updateAdminProduct(editing, payload);
      }
      setEditing(null);
      setPendingPhotoFile(null);
      fetchData();
    } catch (err) {
      showAlert(err.message);
    }
  };

  const remove = async (id) => {
    showConfirm(t('admin.deleteProductConfirm'), async () => {
      try {
        await api.deleteAdminProduct(id);
        fetchData();
      } catch (err) {
        showAlert(err.message);
      }
    });
  };

  return (
    <AdminLayout role="admin">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: '2rem' }}>
        <div>
          <h1 style={S.pageTitle}>{t('admin.products')}</h1>
          <div style={{ width: '40px', height: '2px', background: T.orange }} />
        </div>
        <button
          style={S.btnOrange}
          onClick={openCreate}
          onMouseEnter={(e) => { e.target.style.background = T.orangeHov; }}
          onMouseLeave={(e) => { e.target.style.background = T.orange; }}
        >
          +
          {' '}
          {t('admin.addProduct')}
        </button>
      </div>

      <div style={{ marginBottom: '2rem', padding: '1rem', background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 8 }}>
        <h3 style={{ margin: '0 0 0.75rem', fontSize: '0.9rem', color: T.white }}>{t('admin.productCategories')}</h3>
        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', marginBottom: '0.75rem' }}>
          <select
            value={categoryPanelShopId}
            onChange={(e) => {
              setCategoryPanelShopId(e.target.value);
              setCategoryPanelBranchId('');
            }}
            style={{ ...S.select, width: 'auto', marginBottom: 0, minWidth: 160 }}
          >
            <option value="">{t('admin.chooseSalon')}</option>
            {shops.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
          <select
            value={categoryPanelBranchId}
            onChange={(e) => setCategoryPanelBranchId(e.target.value)}
            style={{ ...S.select, width: 'auto', marginBottom: 0, minWidth: 160 }}
            disabled={!categoryPanelShopId}
          >
            <option value="">{t('admin.chooseBranch')}</option>
            {categoryPanelBranches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
          <button type="button" onClick={openAddCategory} style={S.btnSmall} disabled={!categoryPanelBranchId}>
            {t('admin.addProductCategory')}
          </button>
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
          {productCategories.length === 0 && categoryPanelBranchId && (
            <span style={{ color: T.muted, fontSize: '0.85rem' }}>{t('admin.noProductCategories')}</span>
          )}
          {productCategories.map((c) => (
            <span
              key={c.id}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.4rem',
                padding: '0.3rem 0.6rem',
                background: 'rgba(255,255,255,0.06)',
                borderRadius: 4,
                fontSize: '0.85rem',
              }}
            >
              {c.name}
              <button type="button" onClick={() => openEditCategory(c)} style={{ background: 'none', border: 'none', color: T.orange, cursor: 'pointer', fontSize: '0.75rem' }}>{t('common.edit')}</button>
              <button type="button" onClick={() => removeCategory(c.id)} style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: '0.75rem' }}>{t('common.delete')}</button>
            </span>
          ))}
        </div>
      </div>

      {showCategoryModal && (
        <div style={S.modal}>
          <div style={S.modalBox}>
            <h2 style={S.modalTitle}>{editingCategory ? t('admin.editProductCategory') : t('admin.addProductCategory')}</h2>
            <input placeholder={t('manager.placeholderCategory')} value={categoryForm.name} onChange={(e) => setCategoryForm({ name: e.target.value })} style={S.input} />
            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <button type="button" onClick={saveCategory} style={{ ...S.btnOrange, flex: 1 }}>{t('common.save')}</button>
              <button type="button" onClick={() => setShowCategoryModal(false)} style={S.btnOutline}>{t('common.cancel')}</button>
            </div>
          </div>
        </div>
      )}

      {editing && (
        <div style={S.modal}>
          <div style={S.modalBox}>
            <h2 style={S.modalTitle}>{editing === 'create' ? t('admin.newProduct') : t('admin.editProduct')}</h2>

            <label style={{ display: 'block', fontSize: '0.75rem', color: T.muted, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '0.4rem' }}>1. {t('admin.selectSalon')}</label>
            <select value={selectedShopId} onChange={(e) => handleShopChange(e.target.value)} style={S.select}>
              <option value="">{t('admin.chooseSalon')}</option>
              {shops.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>

            <label style={{ display: 'block', fontSize: '0.75rem', color: T.muted, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '0.4rem' }}>2. {t('admin.selectBranch')}</label>
            <select value={form.branch_id} onChange={(e) => setForm({ ...form, branch_id: e.target.value, category: '' })} style={S.select} disabled={!selectedShopId}>
              <option value="">{selectedShopId ? t('admin.chooseBranch') : t('admin.selectSalonFirst')}</option>
              {filteredBranches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>

            <input placeholder={t('admin.productName')} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} style={S.input} />
            <NumberInput placeholder={t('admin.priceEgp')} value={form.price} onChange={(v) => setForm({ ...form, price: v })} inputStyle={S.input} min={0} step={0.01} />

            <label style={{ display: 'block', fontSize: '0.78rem', color: T.muted, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '0.4rem' }}>{t('admin.category')}</label>
            <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} style={S.input} disabled={!form.branch_id}>
              <option value="">-- {t('admin.selectCategory')} --</option>
              {productCategories.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
            </select>
            <button type="button" onClick={() => { setCategoryPanelBranchId(String(form.branch_id)); openAddCategory(); }} style={{ ...S.btnSmall, marginTop: '0.4rem' }} disabled={!form.branch_id}>
              +
              {' '}
              {t('admin.addNewCategory')}
            </button>

            <div style={{ marginBottom: '1rem', marginTop: '0.75rem' }}>
              <label style={{ display: 'block', fontSize: '0.78rem', color: T.muted, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '0.4rem' }}>{t('manager.photo')}</label>
              {editing === 'create' ? (
                <label style={{ cursor: 'pointer', fontSize: '0.85rem', color: T.orange, textDecoration: 'underline' }}>
                  {pendingPhotoFile ? pendingPhotoFile.name : t('manager.uploadPhoto')}
                  <input type="file" accept="image/*" onChange={(e) => e.target.files?.[0] && setPendingPhotoFile(e.target.files[0])} style={{ display: 'none' }} />
                </label>
              ) : form.photo_url ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
                  <img src={resolveMediaUrl(form.photo_url)} alt="" loading="lazy" style={{ width: 64, height: 64, objectFit: 'contain', background: 'var(--bg-dark)', borderRadius: 8, border: `1px solid ${T.border || 'var(--border)'}` }} />
                  <label style={{ cursor: 'pointer', fontSize: '0.85rem', color: T.orange, textDecoration: 'underline' }}>
                    {t('manager.changePhoto')}
                    <input type="file" accept="image/*" onChange={uploadPhoto} style={{ display: 'none' }} />
                  </label>
                  <button type="button" onClick={deletePhoto} style={{ ...S.btnDanger, padding: '0.35rem 0.6rem', fontSize: '0.75rem' }}>
                    {t('common.delete')}
                  </button>
                </div>
              ) : (
                <label style={{ cursor: 'pointer', fontSize: '0.85rem', color: T.orange, textDecoration: 'underline' }}>
                  {t('manager.uploadPhoto')}
                  <input type="file" accept="image/*" onChange={uploadPhoto} style={{ display: 'none' }} />
                </label>
              )}
            </div>

            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <button type="button" onClick={save} style={{ ...S.btnOrange, flex: 1 }}>{t('common.save')}</button>
              <button type="button" onClick={() => setEditing(null)} style={S.btnOutline}>{t('common.cancel')}</button>
            </div>
          </div>
        </div>
      )}

      {loading ? <Skeleton count={5} height="48px" /> : (
        <div style={{ overflowX: 'auto' }}>
          <table style={S.table}>
            <thead>
              <tr>
                {[t('admin.id'), t('admin.name'), t('admin.salon'), t('admin.branch'), t('admin.price'), t('admin.category'), t('admin.actions')].map((h, i) => <th key={i} style={S.th}>{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {products.map((p) => (
                <tr
                  key={p.id}
                  onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(27,213,173,0.04)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                >
                  <td style={S.td}>{p.id}</td>
                  <td style={{ ...S.td, fontWeight: 500, color: T.white }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      {p.photo_url && <img src={resolveMediaUrl(p.photo_url)} alt="" style={{ width: 32, height: 32, objectFit: 'contain', background: 'var(--bg-dark)', borderRadius: 4 }} />}
                      {p.name}
                    </div>
                  </td>
                  <td style={{ ...S.td, color: T.orange, fontFamily: "'DM Sans', sans-serif", fontSize: '0.82rem', letterSpacing: '0.05em' }}>{shopOfBranch(p.branch_id)}</td>
                  <td style={S.td}>{branchById(p.branch_id)?.name ?? p.branch_id}</td>
                  <td style={{ ...S.td, color: T.orange }}>{p.price} EGP</td>
                  <td style={S.td}>{p.category ?? '—'}</td>
                  <td style={S.td}>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <button type="button" style={S.btnSmall} onClick={() => openEdit(p)}>{t('common.edit')}</button>
                      <button type="button" style={S.btnDanger} onClick={() => remove(p.id)}>{t('common.delete')}</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </AdminLayout>
  );
}

export default function Products() {
  return <ProtectedRoute requireSuperAdmin><ProductsContent /></ProtectedRoute>;
}
